import React, { useContext, useEffect, useReducer, useRef, useState } from 'react'

import './fuel.css';
import SmallCard from '../../components/sections/smallCard';
import LargeCard from '../../components/sections/largeCard';
import { Bar, Line } from 'react-chartjs-2';
import DateRangeSelector from '../../components/sections/dateRangeSelector';
import ItemList from '../../components/sections/itemList';
import FuelEfficiencyCard from '../../components/fuelEfficiencyCard';
import Loader from '../../components/loader';
import { endOfDay, filterFuelRefillsReport, filterFuelReport, formatDate, formatDateRange, startOfDay, subtractDays } from '../../utils/generalFunctions';
import RetryReport from '../../components/retryReport';
import ChartLegends from '../../components/chartLegends';
import { DateContext } from '../../context/dateProvider';

const Fuel = ({ dataReport, devices, updateDevices, progress, error, fetchReport, userConstants, updateUserConstants, isRecoveredReports }) => {
  const { startDate, endDate, today,
    yesterday,
    startActualWeek,
    endActualWeek,
    startPreviousWeek,
    endPreviousWeek,
    startActualMonth,
    endActualMonth,
    startPreviousMonth,
    endPreviousMonth,
  } = useContext(DateContext);

  const [isLoading, setIsLoading] = useState(true);
  const [fuelData, setFuelData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.fuelData : []);
  const [totalData, setTotalData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.fuelTotalData : {});
  const [chartDateRange, setChartDateRange] = useState(0);
  const [activeTab, setActiveTab] = useState('bar');
  const [showChartComparison, setShowChartComparison] = useState(false);
  const [showAllTopHigher, setShowAllTopHigher] = useState(false);
  const [showAllTopLesser, setShowAllTopLesser] = useState(false);
  const [showAllTopHigherEfficiency, setShowAllTopHigherEfficiency] = useState(false);
  const [showAllTopLesserEfficiency, setShowAllTopLesserEfficiency] = useState(false);
  const [showAllTopFuelEfficiency, setShowAllTopFuelEfficiency] = useState(false);
  const [dateTopHigherRange, setDateTopHigherRange] = useState(0);
  const [dateTopLesserRange, setDateTopLesserRange] = useState(0);
  const [dateTopHigherEfficiencyRange, setDateTopHigherEfficiencyRange] = useState(0);
  const [dateTopLesserEfficiencyRange, setDateTopLesserEfficiencyRange] = useState(0);
  const [dateTopRangeFuelEfficiency, setDateTopRangeFuelEfficiency] = useState(0);
  const [dateFuelStatsRange, setDateFuelStatsRange] = useState(0);
  const [displayedHigherData, setDisplayedHigherData] = useState([]);
  const [displayedLesserData, setDisplayedLesserData] = useState([]);
  const [displayedHigherEfficiencyData, setDisplayedHigherEfficiencyData] = useState([]);
  const [displayedLesserEfficiencyData, setDisplayedLesserEfficiencyData] = useState([]);
  const [displayedTopFuelEfficiency, setDisplayedTopFuelEfficiency] = useState([]);
  const chartRef = useRef(null);

  const [levelType, setLevelType] = useState('Nacional');
  const [stateSelected, setStateSelected] = useState('CDMX');

  const [lastFillings, setLastFillings] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.fuelLastFillings : {});

  // const today = new Date(endDate);
  // const yesterday = subtractDays(1, today);
  // const startActualWeek = subtractDays(6, today);
  // const endActualWeek = today;
  // const startActualMonth = subtractDays(29, today);
  // const endActualMonth = today;
  // const startPreviousWeek = subtractDays(13, today);
  // const endPreviousWeek = subtractDays(7, today, true);
  // const startPreviousMonth = subtractDays(59, today);
  // const endPreviousMonth = subtractDays(30, today, true);

  const emptyData = {
    consumed: 0,
    fillingsCount: 0,
    drainsCount: 0,
    mileage_by_gps: 0,
    fillingsVolume: 0,
    start: 0,
    end: 0,
    drainsVolume: 0,
    consumption_per_dist: 0
  };

  useEffect(() => {
    if (!dataReport || dataReport.length === 0) return;
    setIsLoading(true);

    const processedData = filterFuelReport(dataReport, startPreviousMonth, endDate);

    setFuelData(processedData);

    const vehicleRefills = filterFuelRefillsReport(dataReport, startPreviousMonth, endDate);

    setLastFillings(vehicleRefills);

    const accumulatedData = calculateTotal(processedData);

    // Ahora tenemos los totales acumulados en accumulatedData
    setTotalData(accumulatedData);

    updateUserConstants({ "reports.fuelData": processedData, "reports.fuelTotalData": accumulatedData, "reports.fuelLastFillings": vehicleRefills });

    setIsLoading(false);
  }, [dataReport]);

  const calculateTotal = (fuelData) => {
    // Inicializamos los datos acumulados por periodos
    let accumulatedData = {
      "Hoy": {},
      "Ayer": {},
      "Semana actual": {},
      "Semana previa": {},
      "Mes actual": {},
      "Mes previo": {}
    };

    // Inicializamos todos los vehículos con valores 0 en cada periodo
    fuelData.forEach(dayEntry => {
      Object.keys(dayEntry).forEach(vehicle => {
        if (vehicle === "date") return; // Saltamos la fecha, solo queremos los vehículos
        ["Hoy", "Ayer", "Semana actual", "Semana previa", "Mes actual", "Mes previo"].forEach(period => {
          accumulatedData[period][vehicle] = { ...emptyData }; // Inicializamos los vehículos en cada periodo
        });
      });
    });

    // Función para agregar datos a cada periodo
    const addDataToPeriod = (period, carName, row) => {
      accumulatedData[period][carName].consumed += row.consumed || 0;
      accumulatedData[period][carName].fillingsCount += row.fillingsCount || 0;
      accumulatedData[period][carName].drainsCount += row.drainsCount || 0;
      accumulatedData[period][carName].mileage_by_gps += row.mileage_by_gps || 0;
      accumulatedData[period][carName].fillingsVolume += row.fillingsVolume || 0;
      accumulatedData[period][carName].start += row.start || 0;
      accumulatedData[period][carName].end += row.end || 0;
      accumulatedData[period][carName].drainsVolume += row.drainsVolume || 0;
      accumulatedData[period][carName].consumption_per_dist += row.consumption_per_dist || 0;
    };

    // Procesamos los datos para acumular por periodo
    fuelData.forEach(dayEntry => {
      const entryDate = new Date(dayEntry.date); // Parseamos la fecha del processedData

      Object.keys(dayEntry).forEach(carName => {
        if (carName !== 'date') {
          const data = dayEntry[carName] || emptyData;

          // Filtramos los datos según el periodo
          if (entryDate.toDateString() === today.toDateString()) {
            addDataToPeriod("Hoy", carName, data);
          } else if (entryDate.toDateString() === yesterday.toDateString()) {
            addDataToPeriod("Ayer", carName, data);
          }

          if (entryDate >= startActualWeek && entryDate <= endActualWeek) {
            addDataToPeriod("Semana actual", carName, data);
          }

          if (entryDate >= startActualMonth && entryDate <= endActualMonth) {
            addDataToPeriod("Mes actual", carName, data);
          }

          if (entryDate >= startPreviousWeek && entryDate <= endPreviousWeek) {
            addDataToPeriod("Semana previa", carName, data);
          }

          if (entryDate >= startPreviousMonth && entryDate <= endPreviousMonth) {
            addDataToPeriod("Mes previo", carName, data);
          }
        }
      });
    });

    return accumulatedData;
  };

  useEffect(() => {

    const newTotalData = calculateTotal(fuelData);
    setTotalData(newTotalData);

  }, [today, yesterday, startActualWeek, endActualWeek, startPreviousWeek, endPreviousWeek]);

  // Filtrar dispositivos visibles
  const visibleDevices = devices
    .filter(device => device.visible) // Solo los dispositivos con visible: true
    .map(device => device.label); // Obtener los nombres de los vehículos

  // Filtrar lastFillings para incluir solo vehículos visibles
  const filteredLastFillings = Object.fromEntries(
    Object.entries(lastFillings).filter(([vehicle]) =>
      visibleDevices.includes(vehicle)
    )
  );

  const handleCheckboxChange = () => {
    setShowChartComparison(!showChartComparison); // Alternar el estado
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const showZoomMessage = useRef(false);
  const [, forceRender] = useReducer(x => x + 1, 0);
  const zoomTimeout = useRef(null);


  const handleWheel = (e) => {
    if (chartDateRange > 0 && !e.ctrlKey) {
      if (!showZoomMessage.current) {
        showZoomMessage.current = true;
        forceRender();

        if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
        zoomTimeout.current = setTimeout(() => {
          showZoomMessage.current = false;
          forceRender();
        }, 3000);
      }
    }
  };


  const renderContent = () => {
    switch (activeTab) {
      case 'bar':
        return <>
          <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
              Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
            </div>
            <Bar ref={chartRef} data={data} options={options} />
          </div>
          <ChartLegends devices={devices} updateDevices={updateDevices} id={'fuel-bar-'} />
        </>
      case 'line':
        return <>
          <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
              Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
            </div>
            <Line ref={chartRef} data={data} options={options} />
          </div>
          <ChartLegends devices={devices} updateDevices={updateDevices} id={'fuel-line-'} />
        </>
      default:
        return null;
    }
  };

  const filterDataByRange = (range, isPrevious = false) => {
    const filteredData = [];

    let startDate2, endDate2;

    // Elegir fechas según el rango y si es el periodo anterior o actual
    switch (range) {
      case 0:
        startDate2 = isPrevious ? startOfDay(yesterday) : startOfDay(today);
        endDate2 = isPrevious ? endOfDay(yesterday) : endOfDay(today);
        break;
      case 1:
        startDate2 = isPrevious ? startOfDay(startPreviousWeek) : startOfDay(startActualWeek);
        endDate2 = isPrevious ? endOfDay(endPreviousWeek) : startOfDay(endActualWeek);
        break;
      case 2:
        startDate2 = isPrevious ? startOfDay(startPreviousMonth) : startOfDay(startActualMonth);
        endDate2 = isPrevious ? endOfDay(endPreviousMonth) : endOfDay(endActualMonth);
        break;
      default:
        startDate2 = startOfDay(new Date(startDate));
        endDate2 = endOfDay(today);
    }

    // Filtrar los datos de acuerdo al rango seleccionado
    fuelData.forEach(item => {
      if (new Date(item.date) >= startDate2 && new Date(item.date) <= endDate2) {
        filteredData.push(item);
      }
    });

    return filteredData;
  };

  const filteredData = filterDataByRange(chartDateRange);               // Para el rango actual
  const filteredPreviousData = filterDataByRange(chartDateRange, true); // Para el rango anterior

  const validDates = filteredData.filter(day =>
    devices.some(device => device.visible && day[device.label]?.consumed > 0)
  );

  const labels = validDates.map(day => formatDate(day.date));

  const vibrantColors = [];
  devices.forEach(item => {
    vibrantColors.push(item.color);
  });

  const datasets = devices.map(device => device.label)
    .flatMap((label, index) => {
      const currentData = filteredData
        .filter(day => devices.some(d => d.visible && day[d.label]?.consumed > 0))
        .map(day => day[label]?.consumed || 0);

      const previousData = filteredPreviousData
        .filter(day => devices.some(d => d.visible && day[d.label]?.consumed > 0))
        .map(day => day[label]?.consumed || 0);

      const isVisible = devices.find(device => device.label === label)?.visible ?? true;

      return [
        {
          label: `${label}`,
          data: currentData,
          fill: false,
          borderColor: vibrantColors[index % vibrantColors.length],
          backgroundColor: vibrantColors[index % vibrantColors.length],
          tension: 0.1,
          borderWidth: 2,
          hidden: !isVisible // Ocultar según el estado visible del dispositivo
        },
        showChartComparison && {
          label: `${label} (Anterior)`,
          data: previousData,
          fill: false,
          borderColor: vibrantColors[index % vibrantColors.length],
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderDash: [5, 5],
          tension: 0.1,
          borderWidth: 2,
          hidden: !isVisible // Ocultar también para comparación
        }
      ].filter(Boolean);
    }) || [];

  const data = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false,
          text: "Fecha",
          font: {
            size: 16,
            weight: 'bold',
            lineHeight: 1.2,
          },
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: "Consumo (L)",
          font: {
            size: 16,
            weight: 'bold',
            lineHeight: 1.2,
          },
        }
      }
    },
    plugins: {
      zoom: {
        zoom: {
          wheel: {
            enabled: chartDateRange !== 0, // Habilitar zoom con la rueda del ratón
            threshold: 5,
            modifierKey: 'ctrl'
          },
          pinch: {
            enabled: chartDateRange !== 0, // Habilitar zoom con gestos táctiles
            threshold: 2,
          },
          mode: 'x', // Hacer zoom solo en el eje X
        },
        pan: {
          enabled: true, // Habilitar paneo (desplazamiento)
          mode: 'x', // Permitir desplazamiento solo en el eje X
          modifierKey: 'ctrl'
        },
      },
      legend: {
        display: false,
        position: 'bottom',
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer';
        },
        onLeave: (event) => {
          event.native.target.style.cursor = 'default';
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const datasetIndex = legendItem.datasetIndex;
          const dataset = chart.data.datasets[datasetIndex];
          const deviceName = dataset.label.replace(' (Anterior)', '');

          // Alternar visibilidad del dataset
          const isHidden = !dataset.hidden;
          dataset.hidden = isHidden;

          // Actualiza el estado de `devices` para reflejar el cambio
          const updatedDevices = devices.map(device =>
            device.label === deviceName ? { ...device, visible: !isHidden } : device
          );

          updateDevices(updatedDevices);

          // Si `showComparison` está activado, también alterna el dataset de comparación
          if (showChartComparison) {
            const comparisonDataset = chart.data.datasets[datasetIndex + 1];
            if (comparisonDataset && comparisonDataset.label.includes('(Anterior)')) {
              comparisonDataset.hidden = isHidden;
            }
          }

          chart.update(); // Actualiza la gráfica
        },

        labels: {
          usePointStyle: true,
          padding: 15,
          pointStyle: 'circle',
          boxWidth: 8,        // Ajusta el ancho de la caja del punto
          boxHeight: 8,
          font: {
            size: 14
          },
          filter: (legendItem) => !legendItem.text.includes('Anterior'), // Filtra para mostrar solo "Actual"
        },
      },
      title: {
        display: false,
        text: 'Combustible consumido por Flota',
      },
      tooltip: {
        callbacks: {
          title: function (tooltipItems) {
            // Solo tomamos el primer elemento del array tooltipItems
            const tooltipItem = tooltipItems[0];
            const index = tooltipItem.dataIndex;
            const isPreviousPeriod = tooltipItem.dataset.label.includes('(Anterior)');
            const previousDates = filteredPreviousData.map(item => formatDate(item.date));

            // Obtener la fecha correspondiente
            const dateLabel = isPreviousPeriod
              ? previousDates[index] + ' (Previo)' // Si es el periodo anterior, usa `previousDates`
              : labels[index];       // Si es el actual, usa `labels`

            return dateLabel;
          },
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset.label.replace(/\s*\(Actual\)|\s*\(Anterior\)/g, '');
            const value = tooltipItem.raw;

            setValuesPeriod(dataset);

            // Formatea el texto del tooltip
            return `${dataset}: ${value} L`;
          }
        }
      }
    },
  };

  const setValuesPeriod = (dataset) => {
    // Obtener la opción seleccionada del selector
    const selectedOption = chartDateRange
    let currentPeriodFuel, previousPeriodFuel, currentPeriodDates, previousPeriodDates;
    // Actualizar los spans con el kilometraje total y las fechas de los periodos
    if (selectedOption === 0) {
      currentPeriodFuel = calculatePeriodTotalData('Hoy', dataset, 'consumed');
      previousPeriodFuel = calculatePeriodTotalData('Ayer', dataset, 'consumed');

      currentPeriodDates = { startDate: startOfDay(new Date(today)), endDate: today };
      previousPeriodDates = { startDate: yesterday, endDate: subtractDays(1, today, true) };
    } else if (selectedOption === 1) {
      currentPeriodFuel = calculatePeriodTotalData('Semana actual', dataset, 'consumed');
      previousPeriodFuel = calculatePeriodTotalData('Semana previa', dataset, 'consumed');

      currentPeriodDates = { startDate: startActualWeek, endDate: endActualWeek };
      previousPeriodDates = { startDate: startPreviousWeek, endDate: endPreviousWeek };
    } else if (selectedOption === 2) {
      currentPeriodFuel = calculatePeriodTotalData('Mes actual', dataset, 'consumed');
      previousPeriodFuel = calculatePeriodTotalData('Mes previo', dataset, 'consumed');

      currentPeriodDates = { startDate: startActualMonth, endDate: endActualMonth };
      previousPeriodDates = { startDate: startPreviousMonth, endDate: endPreviousMonth };
    }

    const currentPeriodElem = document.getElementById('current-period-fuel');
    const previousPeriodElem = document.getElementById('previous-period-fuel');

    currentPeriodElem.innerText = `${currentPeriodFuel.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`;
    previousPeriodElem.innerText = `${previousPeriodFuel.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`;

    // Remover clases previas
    currentPeriodElem.classList.remove('high', 'normal', 'low');
    previousPeriodElem.classList.remove('high', 'normal', 'low');

    // Cambiar el className según la comparación
    if (currentPeriodFuel > previousPeriodFuel) {
      currentPeriodElem.classList.add('high');
      previousPeriodElem.classList.add('low');
    } else if (currentPeriodFuel < previousPeriodFuel) {
      currentPeriodElem.classList.add('low');
      previousPeriodElem.classList.add('high');
    } else {
      // Si son iguales, ambos valores tendrán la clase 'yellow'
      currentPeriodElem.classList.add('normal');
      previousPeriodElem.classList.add('normal');
    }

    // Formatear las fechas en dd/mm/aaaa - dd/mm/aaaa
    if (selectedOption === 0) {
      document.getElementById('current-period-date-fuel').innerText = `(${formatDate(currentPeriodDates.startDate)})`;
      document.getElementById('previous-period-date-fuel').innerText = `(${formatDate(previousPeriodDates.startDate)})`;
    } else {
      document.getElementById('current-period-date-fuel').innerText = `(${formatDate(currentPeriodDates.startDate)} - ${formatDate(currentPeriodDates.endDate)})`;
      document.getElementById('previous-period-date-fuel').innerText = `(${formatDate(previousPeriodDates.startDate)} - ${formatDate(previousPeriodDates.endDate)})`;
    }
  };

  const calculatePeriodTotalData = (period, car, dataKey) => {
    const periodData = totalData[period];

    if (periodData && periodData[car] && periodData[car][dataKey]) {
      return periodData[car][dataKey]; // Retornar el valor correcto (consumed, time, etc.)
    }

    return 0; // Si no hay datos, retornar 0
  };

  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom(); // Usar la referencia para resetear el zoom
    }
  };

  // Función para alternar entre "Ver todos" y "Ver menos"
  const handleShowAllTopHigherToggle = () => {
    setShowAllTopHigher(!showAllTopHigher);
  };

  const handleShowAllTopLesserToggle = () => {
    setShowAllTopLesser(!showAllTopLesser);
  };

  const handleShowAllTopHigherEfficiencyToggle = () => {
    setShowAllTopHigherEfficiency(!showAllTopHigherEfficiency);
  };

  const handleShowAllTopLesserEfficiencyToggle = () => {
    setShowAllTopLesserEfficiency(!showAllTopLesserEfficiency);
  };

  const handleShowAllTopFuelEfficiencyToggle = () => {
    setShowAllTopFuelEfficiency(!showAllTopFuelEfficiency);
  };

  const getDataByDateRange = (range) => {
    let periodCurrentData = {}, periodPreviousData = {};

    switch (range) {
      case 0:
        periodCurrentData = totalData['Hoy'] || {};
        periodPreviousData = totalData['Ayer'] || {};
        break;
      case 1:
        periodCurrentData = totalData['Semana actual'] || {};
        periodPreviousData = totalData['Semana previa'] || {};
        break;
      case 2:
        periodCurrentData = totalData['Mes actual'] || {};
        periodPreviousData = totalData['Mes previo'] || {};
        break;
      default:
        return [];
    }

    // Filtramos dispositivos visibles
    const visibleDevices = devices.filter(device => device.visible);

    // Filtrar dispositivos visibles y mapear los datos solo para esos dispositivos
    const combinedData = Object.keys({ ...periodCurrentData, ...periodPreviousData })
      .filter(car => visibleDevices.find(device => device.label === car))  // Filtra solo los visibles
      .map((car) => {
        const current = periodCurrentData[car] || emptyData;
        const previous = periodPreviousData[car] || emptyData;

        return {
          car, // Nombre del vehículo
          currentConsumed: current.consumed,
          currentFillingsCount: current.fillingsCount,
          currentDrainsCount: current.drainsCount,
          currentMileage_by_gps: current.mileage_by_gps,
          currentFillingsVolume: current.fillingsVolume,
          currentStart: current.start,
          currentEnd: current.end,
          currentDrainsVolume: current.drainsVolume,
          currentConsumption_per_dist: current.consumption_per_dist,
          currentEfficiency: current.mileage_by_gps > 0 && current.consumed > 0 ? current.mileage_by_gps / current.consumed : 0,

          previousConsumed: previous.consumed,
          previousFillingsCount: previous.fillingsCount,
          previousDrainsCount: previous.drainsCount,
          previousMileage_by_gps: previous.mileage_by_gps,
          previousFillingsVolume: previous.fillingsVolume,
          previousStart: previous.start,
          previousEnd: previous.end,
          previousDrainsVolume: previous.drainsVolume,
          previousConsumption_per_dist: previous.consumption_per_dist,
          previousEfficiency: previous.mileage_by_gps > 0 && previous.consumed > 0 ? previous.mileage_by_gps / previous.consumed : 0,
        };
      });

    return combinedData;
  };

  // Ordenar para obtener los vehículos con más consumo de combustible
  useEffect(() => {
    const sortedHigherFuelData = getDataByDateRange(dateTopHigherRange)
      .sort((a, b) => {
        if (b.currentConsumed === a.currentConsumed) {
          return b.previousConsumed - a.previousConsumed; // Comparar por consumo previos si los actuales son iguales
        }
        return b.currentConsumed - a.currentConsumed; // Ordenar por consumo actual de forma descendente
      });
    setDisplayedHigherData(showAllTopHigher ? sortedHigherFuelData : sortedHigherFuelData.slice(0, 5));
  }, [devices, dateTopHigherRange, totalData, showAllTopHigher]);

  // Ordenar para obtener los vehículos con menos consumo de combustible
  useEffect(() => {
    const sortedLesserFuelData = getDataByDateRange(dateTopLesserRange)
      .sort((a, b) => {
        if (a.currentConsumed === b.currentConsumed) {
          if (a.previousConsumed === b.previousConsumed) {
            return -1;
          }
          return a.previousConsumed - b.previousConsumed; // Comparar por consumo previo si los actuales son iguales
        }
        return a.currentConsumed - b.currentConsumed; // Ordenar por consumo actuale de forma ascendente
      });
    setDisplayedLesserData(showAllTopLesser ? sortedLesserFuelData : sortedLesserFuelData.slice(0, 5));
  }, [devices, dateTopLesserRange, totalData, showAllTopLesser]);

  // Ordenar para obtener los vehículos con más consumo de combustible
  useEffect(() => {
    const sortedHigherFuelEfficiencyData = getDataByDateRange(dateTopHigherEfficiencyRange)
      .sort((a, b) => {
        if (b.currentEfficiency === a.currentEfficiency) {
          return b.previousEfficiency - a.previousEfficiency; // Comparar por consumo previos si los actuales son iguales
        }
        return b.currentEfficiency - a.currentEfficiency; // Ordenar por consumo actual de forma descendente
      });
    setDisplayedHigherEfficiencyData(showAllTopHigherEfficiency ? sortedHigherFuelEfficiencyData : sortedHigherFuelEfficiencyData.slice(0, 5));
  }, [devices, dateTopHigherEfficiencyRange, totalData, showAllTopHigherEfficiency]);

  // Ordenar para obtener los vehículos con menos consumo de combustible
  useEffect(() => {
    const sortedLesserFuelEfficiencyData = getDataByDateRange(dateTopLesserEfficiencyRange)
      .sort((a, b) => {
        if (a.currentEfficiency === b.currentEfficiency) {
          if (a.previousEfficiency === b.previousEfficiency) {
            return -1;
          }
          return a.previousEfficiency - b.previousEfficiency; // Comparar por consumo previo si los actuales son iguales
        }
        return a.currentEfficiency - b.currentEfficiency; // Ordenar por consumo actuale de forma ascendente
      });
    setDisplayedLesserEfficiencyData(showAllTopLesserEfficiency ? sortedLesserFuelEfficiencyData : sortedLesserFuelEfficiencyData.slice(0, 5));
  }, [devices, dateTopLesserEfficiencyRange, totalData, showAllTopLesserEfficiency]);

  useEffect(() => {
    setDisplayedTopFuelEfficiency(getDataByDateRange(dateTopRangeFuelEfficiency));
  }, [devices, totalData, dateTopRangeFuelEfficiency]);

  // console.log(totalData);
  const totalItems = displayedHigherData.length;

  // Calcular el ancho basado en la cantidad de dígitos
  const indexWidth = totalItems < 10 ? '2ch' : totalItems < 100 ? '3ch' : '4ch';

  const fuelStatsData = () => {
    let periodCurrentData = {};
    let periodPreviousData = {};

    // Determina los periodos actuales y previos según el rango seleccionado
    switch (dateFuelStatsRange) {
      case 0:
        periodCurrentData = totalData['Hoy'] || {};
        periodPreviousData = totalData['Ayer'] || {};
        break;
      case 1:
        periodCurrentData = totalData['Semana actual'] || {};
        periodPreviousData = totalData['Semana previa'] || {};
        break;
      case 2:
        periodCurrentData = totalData['Mes actual'] || {};
        periodPreviousData = totalData['Mes previo'] || {};
        break;
      default:
        break;
    }

    // Filtramos dispositivos visibles
    const visibleDevices = devices.filter(device => device.visible);

    // Función para calcular totales acumulados y promedios
    const calculateStats = (periodData) => {
      let totals = {};
      let consumptionPerDistValues = []; // Para calcular el promedio del consumo por distancia
      let efficiencies = []; // Para almacenar las eficiencias individuales

      // Iteramos sobre los vehículos visibles
      visibleDevices.forEach(device => {
        const carData = periodData[device.label]; // Extraemos los datos del vehículo visible

        if (carData) {
          // Acumulamos los valores para las métricas relevantes
          Object.keys(carData).forEach(key => {
            if (key === "consumed" || key === "mileage_by_gps") {
              totals[key] = (totals[key] || 0) + carData[key];
            } else {
              totals[key] = (totals[key] || 0) + carData[key];
            }
          });

          // Calculamos la eficiencia (km/L) si mileage_by_gps y consumed son válidos
          if (carData.mileage_by_gps > 0 && carData.consumed > 0) {
            const efficiency = carData.mileage_by_gps / carData.consumed;
            efficiencies.push(efficiency);
          }

          // Calculamos el consumo por cada 100 km si mileage_by_gps es mayor a 0
          if (carData.mileage_by_gps > 0) {
            const consumptionPer100Km = (carData.consumed / carData.mileage_by_gps);
            consumptionPerDistValues.push(consumptionPer100Km);
          }
        }
      });

      // Calcular el promedio de consumption_per_dist
      if (consumptionPerDistValues.length > 0) {
        totals.consumption_per_dist =
          consumptionPerDistValues.reduce((sum, value) => sum + value, 0) / consumptionPerDistValues.length;
      } else {
        totals.consumption_per_dist = 0; // Si no hay valores válidos, el promedio es 0
      }

      // Calcular el promedio de eficiencia (km/L)
      if (efficiencies.length > 0) {
        totals.efficiency = efficiencies.reduce((sum, value) => sum + value, 0) / efficiencies.length;
      } else {
        totals.efficiency = 0; // Si no hay valores válidos, la eficiencia es 0
      }

      return totals;
    };

    const currentTotals = calculateStats(periodCurrentData);
    const previousTotals = calculateStats(periodPreviousData);

    return { periodCurrentData: currentTotals, periodPreviousData: previousTotals };
  };

  const fuelStats = fuelStatsData();
  // console.log(fuelData);

  const [cardsReady, setCardsReady] = useState([]);

  useEffect(() => {
    const totalCards = 11; // Cambiar este número según la cantidad de tarjetas
    if (progress === 100) {
      // Generar tiempos aleatorios para cada tarjeta
      const randomDelays = Array.from({ length: totalCards }, () => Math.random() * 2000 + 1000); // 1-3 segundos
      const timeouts = randomDelays.map((delay, index) => {
        return setTimeout(() => {
          setCardsReady((prev) => {
            const updated = [...prev];
            updated[index] = true;
            return updated;
          });
        }, delay);
      });

      // Limpiar los timeouts al desmontar
      return () => timeouts.forEach((timeout) => clearTimeout(timeout));
    } else {
      setCardsReady(new Array(totalCards).fill(false));
    }
  }, [progress]);

  return (
    <div className='section_grid_container'>
      <LargeCard
        variant='fuel'
        dateRange={chartDateRange}
        setDateRange={setChartDateRange}
        showComparison={showChartComparison}
        setShowChartComparison={setShowChartComparison}
        handleCheckboxChange={handleCheckboxChange}
        handleTabChange={handleTabChange}
        renderContent={renderContent}
        activeTab={activeTab}
        resetZoom={resetZoom}
        progress={progress}
        isReady={!cardsReady[0]}
        error={error}
        fetchReport={fetchReport}
        sectionNum={4}
      />
      <div className='dashboard_card card_top card_short'>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[1] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='card_header_flex'>
                <div className='dashboard_card_header_container'>
                  <span className='dashboard_card_title'>{showAllTopHigher ? 'Todos los' : 'Top 5'} vehículos con mayor consumo de combustible</span>
                  <button className='dashboard_card_view_more' onClick={handleShowAllTopHigherToggle}>
                    {showAllTopHigher ? 'Ver menos' : 'Ver todos'}
                  </button>
                </div>
                <div className='card_config_container'>
                  <DateRangeSelector dateRange={dateTopHigherRange} setDateRange={setDateTopHigherRange} />
                </div>
              </div>
              <div className='dashboard_card_content_container'>
                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                  <div className='top_items_header_container'>
                    <span>Periodo</span>
                    <span>Actual<br /><span>({formatDateRange(dateTopHigherRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                    <span>Previo<br /><span>({formatDateRange(dateTopHigherRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                  </div>
                  {
                    displayedHigherData.length === 0 ?
                      <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                      :
                      displayedHigherData.map((item, index) => (
                        <ItemList
                          key={index}
                          index={index + 1}
                          type={'fuel'}
                          name={item.car} // Nombre del vehículo
                          data={{
                            currentConsumed: item.currentConsumed,
                            previousConsumed: item.previousConsumed
                          }}

                        />
                      ))
                  }
                </div>
              </div>
            </>
          )}
      </div>
      <div className='dashboard_card card_top card_short'>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[2] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='card_header_flex'>
                <div className='dashboard_card_header_container'>
                  <span className='dashboard_card_title'>{showAllTopLesser ? 'Todos los' : 'Top 5'} vehículos con menor consumo de combustible</span>
                  <button className='dashboard_card_view_more' onClick={handleShowAllTopLesserToggle}>
                    {showAllTopLesser ? 'Ver menos' : 'Ver todos'}
                  </button>
                </div>
                <div className='card_config_container'>
                  <DateRangeSelector dateRange={dateTopLesserRange} setDateRange={setDateTopLesserRange} />
                </div>
              </div>
              <div className='dashboard_card_content_container'>
                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                  <div className='top_items_header_container'>
                    <span>Periodo</span>
                    <span>Actual<br /><span>({formatDateRange(dateTopLesserRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                    <span>Previo<br /><span>({formatDateRange(dateTopLesserRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                  </div>
                  {
                    displayedLesserData.length === 0 ?
                      <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                      :
                      displayedLesserData.map((item, index) => (
                        <ItemList
                          key={index}
                          index={index + 1}
                          type={'fuel'}
                          name={item.car} // Nombre del vehículo
                          data={{
                            currentConsumed: item.currentConsumed,
                            previousConsumed: item.previousConsumed
                          }}
                        />
                      ))
                  }
                </div>
              </div>
            </>
          )}
      </div>
      <div className='fuel_stats_container'>
        <DateRangeSelector dateRange={dateFuelStatsRange} setDateRange={setDateFuelStatsRange} />
        <div className='small_cards_container'>
          <SmallCard
            title='Consumo por distancia promedio'
            data={{
              currentValue: fuelStats.periodCurrentData.efficiency || 0,
              previousValue: fuelStats.periodPreviousData.efficiency || 0
            }}
            unit='Km/L'
            progress={progress}
            isReady={!cardsReady[3]}
            error={error}
            fetchReport={fetchReport}
          />
          <SmallCard
            title='Consumos'
            data={{
              currentValue: fuelStats.periodCurrentData.consumed || 0,
              previousValue: fuelStats.periodPreviousData.consumed || 0
            }}
            unit='L'
            progress={progress}
            isReady={!cardsReady[4]}
            error={error}
            fetchReport={fetchReport}
          />
          <SmallCard
            title='Llenados'
            data={{
              currentValue: fuelStats.periodCurrentData.fillingsVolume || 0,
              previousValue: fuelStats.periodPreviousData.fillingsVolume || 0
            }}
            unit='L'
            progress={progress}
            isReady={!cardsReady[5]}
            error={error}
            fetchReport={fetchReport}
          />
          <SmallCard
            title='Drenados'
            data={{
              currentValue: fuelStats.periodCurrentData.drainsVolume || 0,
              previousValue: fuelStats.periodPreviousData.drainsVolume || 0
            }}
            unit='L'
            progress={progress}
            isReady={!cardsReady[6]}
            error={error}
            fetchReport={fetchReport}
          />
        </div>
      </div>
      <div className='dashboard_card card_top card_short'>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[7] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='card_header_flex'>
                <div className='dashboard_card_header_container'>
                  <span className='dashboard_card_title'>{showAllTopHigherEfficiency ? 'Todos los' : 'Top 5'} vehículos con mayor rendimiento</span>
                  <button className='dashboard_card_view_more' onClick={handleShowAllTopHigherEfficiencyToggle}>
                    {showAllTopHigherEfficiency ? 'Ver menos' : 'Ver todos'}
                  </button>
                </div>
                <div className='card_config_container'>
                  <DateRangeSelector dateRange={dateTopHigherEfficiencyRange} setDateRange={setDateTopHigherEfficiencyRange} />
                </div>
              </div>
              <div className='dashboard_card_content_container'>
                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                  <div className='top_items_header_container'>
                    <span>Periodo</span>
                    <span>Actual<br /><span>({formatDateRange(dateTopHigherEfficiencyRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                    <span>Previo<br /><span>({formatDateRange(dateTopHigherEfficiencyRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                  </div>
                  {
                    displayedHigherEfficiencyData.length === 0 ?
                      <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                      :
                      displayedHigherEfficiencyData.map((item, index) => (
                        <ItemList
                          key={index}
                          index={index + 1}
                          type={'fuel_efficiency'}
                          name={item.car} // Nombre del vehículo
                          data={{
                            currentEfficiency: item.currentEfficiency,
                            previousEfficiency: item.previousEfficiency
                          }}

                        />
                      ))
                  }
                </div>
              </div>
            </>
          )}
      </div>
      <div className='dashboard_card card_top card_short'>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[8] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='card_header_flex'>
                <div className='dashboard_card_header_container'>
                  <span className='dashboard_card_title'>{showAllTopLesserEfficiency ? 'Todos los' : 'Top 5'} vehículos con menor rendimiento</span>
                  <button className='dashboard_card_view_more' onClick={handleShowAllTopLesserEfficiencyToggle}>
                    {showAllTopLesserEfficiency ? 'Ver menos' : 'Ver todos'}
                  </button>
                </div>
                <div className='card_config_container'>
                  <DateRangeSelector dateRange={dateTopLesserEfficiencyRange} setDateRange={setDateTopLesserEfficiencyRange} />
                </div>
              </div>
              <div className='dashboard_card_content_container'>
                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                  <div className='top_items_header_container'>
                    <span>Periodo</span>
                    <span>Actual<br /><span>({formatDateRange(dateTopLesserEfficiencyRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                    <span>Previo<br /><span>({formatDateRange(dateTopLesserEfficiencyRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                  </div>
                  {
                    displayedLesserEfficiencyData.length === 0 ?
                      <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                      :
                      displayedLesserEfficiencyData.map((item, index) => (
                        <ItemList
                          key={index}
                          index={index + 1}
                          type={'fuel_efficiency'}
                          name={item.car} // Nombre del vehículo
                          data={{
                            currentEfficiency: item.currentEfficiency,
                            previousEfficiency: item.previousEfficiency
                          }}
                        />
                      ))
                  }
                </div>
              </div>
            </>
          )}
      </div>
      <div className='dashboard_card card_top_large card_short' style={{ height: 'auto' }}>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[9] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='card_header_flex'>
                <div className='dashboard_card_header_container'>
                  <span className='dashboard_card_title'>Consumo de combustible</span>
                  <button className='dashboard_card_view_more' style={{ marginBottom: 0 }} onClick={handleShowAllTopFuelEfficiencyToggle}>
                    {showAllTopFuelEfficiency ? 'Ver menos' : 'Ver todos'}
                  </button>
                </div>
                <div className='card_config_container'>
                  <DateRangeSelector dateRange={dateTopRangeFuelEfficiency} setDateRange={setDateTopRangeFuelEfficiency} />
                </div>
              </div>
              {/* <div className='level_type_container'>
                <div>
                  <span>Precios promedio a nivel </span>
                  <label>
                    <input
                      type='radio'
                      name='nivelPrecio'
                      value='Nacional'
                      checked={levelType === 'Nacional'}
                      onChange={() => {
                        setLevelType('Nacional');
                      }}
                    />
                    <span>Nacional</span>
                  </label>
                  <label>
                    <input
                      type='radio'
                      name='nivelPrecio'
                      value='Estatal'
                      checked={levelType === 'Estatal'}
                      onChange={() => {
                        setLevelType('Estatal');
                      }}
                    />
                    <span>Estatal</span>
                  </label>
                  {levelType === 'Estatal' && (
                    <select
                      value={stateSelected}
                      onChange={(e) => {
                        setStateSelected(e.target.value);
                      }}
                      className='selector'
                    >
                      <option value='AGS'>Aguascalientes</option>
                      <option value='BC'>Baja California</option>
                      <option value='BCS'>Baja California Sur</option>
                      <option value='CAMP'>Campeche</option>
                      <option value='CHIS'>Chiapas</option>
                      <option value='CHIH'>Chihuahua</option>
                      <option value='CDMX'>Ciudad de México</option>
                      <option value='COAH'>Coahuila de Zaragoza</option>
                      <option value='COL'>Colima</option>
                      <option value='DGO'>Durango</option>
                      <option value='MEX'>Estado de México</option>
                      <option value='GTO'>Guanajuato</option>
                      <option value='GRO'>Guerrero</option>
                      <option value='HGO'>Hidalgo</option>
                      <option value='JAL'>Jalisco</option>
                      <option value='MICH'>Michoacán de Ocampo</option>
                      <option value='MOR'>Morelos</option>
                      <option value='NAY'>Nayarit</option>
                      <option value='NL'>Nuevo León</option>
                      <option value='OAX'>Oaxaca</option>
                      <option value='PUE'>Puebla</option>
                      <option value='QRO'>Querétaro</option>
                      <option value='QROO'>Quintana Roo</option>
                      <option value='SLP'>San Luis Potosí</option>
                      <option value='SIN'>Sinaloa</option>
                      <option value='SON'>Sonora</option>
                      <option value='TAB'>Tabasco</option>
                      <option value='TAMPS'>Tamaulipas</option>
                      <option value='TLAX'>Tlaxcala</option>
                      <option value='VER'>Veracruz</option>
                      <option value='YUC'>Yucatán</option>
                      <option value='ZAC'>Zacatecas</option>
                    </select>
                  )}
                </div>
                <div style={{ width: '350px', height: '210px' }}>
                  <iframe src={levelType === 'Nacional' ? 'https://petrointelligence.com/api/api_precios.html?consulta=nac' : `https://petrointelligence.com/api/api_precios.html?consulta=estado&estado=${stateSelected}`} width="100%" height="100%" title='Precios de la gasolina'></iframe>
                </div>
              </div> */}
              <div className='dashboard_card_content_container' style={{ height: '100%' }}>
                <FuelEfficiencyCard devices={devices} displayedTopFuelEfficiency={displayedTopFuelEfficiency} showAllTopFuelEfficiency={showAllTopFuelEfficiency} userConstants={userConstants} updateUserConstants={updateUserConstants} />
              </div>
            </>
          )}
      </div>

      <div className='dashboard_card card_chart card_large'>
        {error ?
          <RetryReport fetchReport={fetchReport} sectionNum={4} />
          :
          progress < 100 || !cardsReady[10] ? (
            <Loader progress={progress} />
          ) : (
            <>
              <div className='dashboard_card_header_container'>
                <span className='dashboard_card_title'>Últimos 5 aumentos de combustible registrados</span>
                <div className='config_chart_container'>

                </div>
              </div>
              <div className='dashboard_card_content_container'>
                {Object.keys(filteredLastFillings).length === 0 ?
                  <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                  : (
                    <div className='table_container' style={{ maxHeight: '500px', height: 'auto' }}>
                      <table className='fuel_table'>
                        <thead>
                          <tr>
                            <th>Vehículo</th>
                            <th>Relleno 1</th>
                            <th>Relleno 2</th>
                            <th>Relleno 3</th>
                            <th>Relleno 4</th>
                            <th>Relleno 5</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(filteredLastFillings).map(([vehicle, fillings]) => {
                            // Obtener los valores para calcular el máximo y mínimo
                            const volumes = fillings.map((filling) => filling.volume).filter((v) => v > 0);
                            const maxVolume = Math.max(...volumes);
                            const minVolume = Math.min(...volumes);

                            return (
                              <tr key={vehicle}>
                                <td>{vehicle}</td>
                                {fillings.map((filling, index) => {
                                  if (!filling.volume) {
                                    return <td key={`empty-${index}`}>-</td>
                                  }

                                  const isMax = filling.volume === maxVolume;
                                  const isMin = filling.volume === minVolume;

                                  return (
                                    <td key={index}>
                                      <span
                                        style={{
                                          color: isMax ? 'var(--high-level)' : isMin ? 'var(--low-level)' : '',
                                        }}
                                      >
                                        {filling.volume ? `${filling.volume} L` : '-'}
                                      </span>
                                      <br />
                                      <small>
                                        {filling.date
                                          ? new Date(filling.date).toLocaleDateString('es-ES', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                          : '-'}
                                        <br />
                                        {filling.date
                                          ? new Date(filling.date).toLocaleTimeString('es-ES', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                          })
                                          : '-'}
                                      </small>
                                    </td>
                                  );
                                })
                                }
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </div>
            </>
          )}
      </div>

      {/* <div style={{ height: '200px', overflow: 'auto' }} onClick={(e) => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(e.target);
          selection.removeAllRanges();
          selection.addRange(range);
        }}><pre>{JSON.stringify(dataReport, 0, 2)}</pre></div> */}
    </div >
  )
}

export default Fuel
